import './TicketShowFailReports.scss';

import { DateField } from '@refinedev/antd';
import { useList } from '@refinedev/core';
import { Card, Empty, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import FailReportStatusTag from '@/components/failReport/FailReportStatusTag';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { FailReport, FailReportWarrantyString } from '@/model/FailReport/FailReport';
import { Ticket } from '@/model/Ticket/Ticket';
import { RESOURCES } from '@/Resources';

interface TicketShowFailReportsProps {
  record?: Ticket;
}

const TicketShowFailReports = (props: TicketShowFailReportsProps) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const { record } = props;
  const {
    result: { data: failReports },
    query: { isLoading },
  } = useList<FailReport>({
    resource: RESOURCES.FAIL_REPORTS,
    filters: [{ field: 'ticket', operator: 'eq', value: record?.id }],
    queryOptions: {
      enabled: !!record?.id,
    },
  });
  const getCategoryLabel = (failReport: FailReport) => {
    const categoryName =
      typeof failReport.category === 'object'
        ? (failReport.category as any)?.title
        : failReport.category;

    return categoryName ? `${categoryName}` : '-';
  };

  const getSubcategoryLabel = (failReport: FailReport) => {
    if (!failReport.subcategory) return '-';

    const subcategoryName =
      typeof failReport.subcategory === 'object'
        ? (failReport.subcategory as any)?.title
        : failReport.subcategory;

    return subcategoryName ? `${subcategoryName}` : '-';
  };

  if (isLoading) {
    return (
      <div className="TicketShowFailReports">
        <div className="TicketShowFailReportsCardsContainer">
          {[1, 2].map(i => (
            <Card key={i}>
              <Skeleton active paragraph={{ rows: 3 }} />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="TicketShowFailReports">
      <div className="TicketShowFailReportsCardsContainer">
        {failReports.length > 0 ? (
          failReports.map(fr => (
            <Card className="TicketShowFailReportsCard" key={fr.id}>
              <div className="TicketShowFailReportsCardHeader">
                <div className="TicketShowFailReportsCardTitle">
                  {hasPermission('show', RESOURCES.FAIL_REPORTS) ? (
                    <Link
                      className="TicketShowFailReportsCardLink"
                      to={`/${RESOURCES.FAIL_REPORTS}/show/${fr.id}`}>
                      {t('Reporte')} #{fr.id}
                    </Link>
                  ) : (
                    <span>
                      {t('Reporte')} #{fr.id}
                    </span>
                  )}
                  {fr.status != undefined && <FailReportStatusTag failReportStatus={fr.status} />}
                </div>
                <div className="TicketShowFailReportsCardMeta">
                  <div>
                    <b>{t('Fecha')}:</b> <DateField value={fr.report_date} />
                  </div>
                  {fr.warranty != undefined && (
                    <div>
                      <b>{t('Garantía')}:</b> {t(FailReportWarrantyString[fr.warranty])}
                    </div>
                  )}
                </div>
              </div>
              <div className="TicketShowFailReportsCardDetails">
                <div>
                  <b>{t('Categoría')}:</b> {getCategoryLabel(fr)}
                </div>
                <div>
                  <b>{t('Subcategoría')}:</b> {getSubcategoryLabel(fr)}
                </div>
              </div>
              {fr.comments ? (
                <div className="TicketShowFailReportsCardDescription">
                  <b>{t('Comentarios')}:</b> {fr.comments}
                </div>
              ) : null}
            </Card>
          ))
        ) : (
          <Empty description={t('No hay reportes de problemas asociados')} />
        )}
      </div>
      {hasPermission('create', RESOURCES.FAIL_REPORTS) && record?.id && (
        <Link
          className="TicketShowFailReportsLink"
          to={`/${RESOURCES.FAIL_REPORTS}/create?ticket=${record.id}`}>
          {t('Crear nuevo reporte de problema')}
        </Link>
      )}
    </div>
  );
};

export default TicketShowFailReports;
